import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Instagram, Facebook, Twitter, Youtube } from 'lucide-react';

// Custom Link that scrolls to top while preserving browser history
const ScrollLink = ({ to, children, className }) => {
  const navigate = useNavigate();
  const handleClick = (e) => {
    e.preventDefault();
    navigate(to);
    setTimeout(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }, 0);
  };
  return <Link to={to} onClick={handleClick} className={className}>{children}</Link>;
};

export const Footer = () => {
  const { t, i18n } = useTranslation();

  return (
    <footer className="bg-primary text-primary-foreground" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="md:col-span-1">
            <div className="flex items-center gap-2 mb-4">
              <h3 className="text-xl font-bold tracking-tight">
                <span className="text-white">X</span>
                <span className="text-white font-serif">Perience</span>
              </h3>
            </div>
            <p className="text-primary-foreground/80 text-sm leading-relaxed italic mb-6">
              "Voyagez autrement, rencontrez vraiment."
            </p>
            <div className="flex gap-4">
              <a href="#" className="hover:text-accent transition-colors" aria-label="Instagram">
                <Instagram className="w-5 h-5" />
              </a>
              <a href="#" className="hover:text-accent transition-colors" aria-label="Facebook">
                <Facebook className="w-5 h-5" />
              </a>
              <a href="#" className="hover:text-accent transition-colors" aria-label="Twitter">
                <Twitter className="w-5 h-5" />
              </a>
              <a href="#" className="hover:text-accent transition-colors" aria-label="Youtube">
                <Youtube className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Company */}
          <div className="md:pl-8">
            <h4 className="font-semibold mb-4">{t('footer.company')}</h4>
            <ul className="space-y-3 text-sm text-primary-foreground/80">
              <li><ScrollLink to="/about" className="hover:text-accent transition-colors">{t('footer.about')}</ScrollLink></li>
              <li><ScrollLink to="/founder" className="hover:text-accent transition-colors">{i18n.language === 'fr' ? 'Le fondateur' : 'The Founder'}</ScrollLink></li>
              <li><ScrollLink to="/partenaires" className="hover:text-accent transition-colors">{i18n.language === 'fr' ? 'Partenaires' : 'Partners'}</ScrollLink></li>
              <li><ScrollLink to="/careers" className="hover:text-accent transition-colors">{t('footer.careers')}</ScrollLink></li>
              <li><ScrollLink to="/press" className="hover:text-accent transition-colors">{t('footer.press')}</ScrollLink></li>
            </ul>
          </div>

          {/* Support */}
          <div>
            <h4 className="font-semibold mb-4">{t('footer.support')}</h4>
            <ul className="space-y-3 text-sm text-primary-foreground/80">
              <li><ScrollLink to="/comment-ca-marche" className="hover:text-accent transition-colors">Comment ça marche</ScrollLink></li>
              <li><ScrollLink to="/help" className="hover:text-accent transition-colors">{t('footer.help')}</ScrollLink></li>
              <li><ScrollLink to="/contact" className="hover:text-accent transition-colors">{t('footer.contact')}</ScrollLink></li>
              <li><ScrollLink to="/guide-faq" className="hover:text-accent transition-colors">Guide des Explorateurs</ScrollLink></li>
              <li><ScrollLink to="/nous-soutenir" className="hover:text-accent transition-colors flex items-center gap-1">Nous soutenir ❤️</ScrollLink></li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="font-semibold mb-4">{t('footer.legal')}</h4>
            <ul className="space-y-3 text-sm text-primary-foreground/80">
              <li><ScrollLink to="/terms" className="hover:text-accent transition-colors">{t('footer.terms')}</ScrollLink></li>
              <li><ScrollLink to="/privacy" className="hover:text-accent transition-colors">{t('footer.privacy')}</ScrollLink></li>
              <li><ScrollLink to="/guide-charter" className="hover:text-accent transition-colors">{i18n.language === 'fr' ? 'Charte des Guides' : 'Guide Charter'}</ScrollLink></li>
              <li><ScrollLink to="/traveler-charter" className="hover:text-accent transition-colors">{i18n.language === 'fr' ? 'Charte des Voyageurs' : 'Traveler Charter'}</ScrollLink></li>
            </ul>
          </div>
        </div>

        {/* Partners Section */}
        <div className="mt-12 pt-8 border-t border-primary-foreground/20">
          <div className="flex flex-col md:flex-row items-center justify-center gap-6">
            <span className="text-sm text-primary-foreground/60">
              {i18n.language === 'fr' ? 'Nos partenaires :' : 'Our partners:'}
            </span>
            <div className="flex flex-wrap justify-center items-center gap-4">
              {/* Placeholder logos - white/transparent style for dark footer */}
              <div className="w-16 h-10 bg-white/10 rounded flex items-center justify-center">
                <span className="text-white/40 text-[9px]">Logo</span>
              </div>
              <div className="w-16 h-10 bg-white/10 rounded flex items-center justify-center">
                <span className="text-white/40 text-[9px]">Logo</span>
              </div>
              <div className="w-16 h-10 bg-white/10 rounded flex items-center justify-center">
                <span className="text-white/40 text-[9px]">Logo</span>
              </div>
              <div className="w-16 h-10 bg-white/10 rounded flex items-center justify-center">
                <span className="text-white/40 text-[9px]">Logo</span>
              </div>
            </div>
            <ScrollLink to="/partners" className="text-xs text-primary-foreground/60 hover:text-white transition-colors">
              {i18n.language === 'fr' ? '→ Devenir partenaire' : '→ Become a partner'}
            </ScrollLink>
          </div>
        </div>

        <div className="border-t border-primary-foreground/20 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-primary-foreground/60">
            {t('footer.copyright')}
          </p>
        </div>
      </div>
    </footer>
  );
};
